import { useEffect, useMemo, useReducer } from 'react'
import {
  Box,
  Checkbox,
  Table as MUITable,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material'
import PropTypes from 'prop-types'
import { usePersistedStorage } from '~/hooks'

const styles = {
  narrow: {
    padding: '0.125rem 0.5rem',
    width: '1px',
  },
  normal: { border: 0, padding: '0.125rem 0.25rem' },
}

const defaultPagination = {
  persistedHeight: false,
  rowsPerPage: [10, 25, 50, 100, { label: '1k+', value: -1 }],
}

const actions = {
  PAGE: 'PAGE',
  ROWS_PER_PAGE: 'ROWS_PER_PAGE',
  SELECTED: 'SELECTED',
}

const reducer = (state, action) => {
  switch (action.type) {
    case actions.PAGE:
      return { ...state, page: action.value }
    case actions.ROWS_PER_PAGE:
      return { ...state, page: 0, rowsPerPage: action.value }
    case actions.SELECTED:
      return { ...state, selected: action.value }
    default:
      return { ...state }
  }
}

const Table = (props) => {
  const {
    border,
    container: { component: ContainerComponent = Box, ...container } = {},
    headers,
    hover,
    index,
    pagination = defaultPagination,
    rows: rowsProp,
    selectable = false,
    tableId,
    onSelect: onSelectProp,
    ...rest
  } = props

  const [tableState, setTableState] = usePersistedStorage(
    `table-${tableId}`,
    {}
  )

  const reducerBag = useReducer(reducer, {
    page: 0,
    rowsPerPage: pagination?.rowsPerPage?.[0],
    selected: [],
    ...tableState,
  })

  const [state, dispatch] = reducerBag
  useEffect(() => {
    setTableState({ rowsPerPage: state.rowsPerPage })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.rowsPerPage])
  const { page, rowsPerPage, selected } = state

  const rows = useMemo(() => {
    return rowsProp.map((row, index) => ({
      index: index + 1,
      ...row,
    }))
  }, [rowsProp])

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0

  const handleChangePage = (event, value) => {
    dispatch({ type: actions.PAGE, value })
  }

  const handleChangeRowsPerPage = (event) => {
    dispatch({
      type: actions.ROWS_PER_PAGE,
      value: parseInt(event.target.value, 10),
    })
  }

  const handleSelectAllClick = (event) => {
    const newSelected = event.target.checked
      ? Array.from({ length: rows.length }, (e, i) => i)
      : []
    dispatch({ type: actions.SELECTED, value: newSelected })
    onSelect(newSelected)
  }

  const handleClick = (event, index) => {
    let newSelected = [...selected]
    const selectedIndex = newSelected.indexOf(index)
    if (selectedIndex >= 0) {
      newSelected.splice(selectedIndex, 1)
    } else {
      if (selectable.multiselect) {
        newSelected.push(index)
        newSelected.sort()
      } else {
        newSelected = [index]
      }
    }
    dispatch({ type: actions.SELECTED, value: newSelected })
    onSelect(newSelected)
  }

  const onSelect = (selected) => {
    if (!onSelectProp) return
    onSelectProp(selectable.multiselect ? selected : (selected[0] ?? undefined))
  }

  const getCellStyles = (type, ...other) => {
    const base = styles[type]
    let result = { ...base, border: border ? undefined : 0 }
    other.filter(Boolean).forEach((sx) => {
      result = {
        ...result,
        ...sx,
      }
    })
    return result
  }

  const isSelected = (index) => selectable && selected.indexOf(index) !== -1

  const visibleRows = useMemo(() => {
    return rowsPerPage > 0
      ? rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
      : rows
  }, [page, rows, rowsPerPage])

  return (
    <ContainerComponent {...container}>
      <MUITable {...rest}>
        {headers && (
          <TableHead>
            <TableRow>
              {index && (
                <TableCell align="right" sx={getCellStyles('narrow')}>
                  <Typography variant="header">#</Typography>
                </TableCell>
              )}
              {selectable && (
                <TableCell sx={getCellStyles('narrow', { padding: 0 })}>
                  {selectable.multiselect !== false && (
                    <Checkbox
                      checked={selected.length === rows.length}
                      size="small"
                      sx={{ padding: 0, transform: 'translateY(-0.075em)' }}
                      onClick={handleSelectAllClick}
                    />
                  )}
                </TableCell>
              )}
              {headers.map((item, i) => {
                const { content, narrow, ...rest } = item
                return (
                  <TableCell
                    key={i}
                    {...rest}
                    sx={getCellStyles(
                      narrow ? 'narrow' : 'normal',
                      { whiteSpace: 'nowrap' },
                      rest.sx
                    )}
                  >
                    <Typography variant="header">{content}</Typography>
                  </TableCell>
                )
              })}
            </TableRow>
          </TableHead>
        )}
        <TableBody>
          {visibleRows.map((row, i) => {
            const isItemSelected = isSelected(row.index)
            return (
              <TableRow
                key={i}
                aria-checked={isItemSelected}
                hover={hover}
                selected={isItemSelected}
                sx={{ cursor: selectable ? 'pointer' : undefined }}
                onClick={(event) => handleClick(event, row.index)}
              >
                {index && (
                  <TableCell align="right" sx={getCellStyles('narrow')}>
                    {row.index}.
                  </TableCell>
                )}
                {selectable && (
                  <TableCell sx={getCellStyles('narrow', { padding: 0 })}>
                    <Checkbox
                      checked={isItemSelected}
                      size="small"
                      sx={{ padding: 0, transform: 'translateY(-0.075em)' }}
                    />
                  </TableCell>
                )}
                {row.cells?.map((cell, j) => {
                  const { content, narrow, ...rest } = cell
                  return (
                    <TableCell
                      key={j}
                      {...rest}
                      sx={getCellStyles(narrow ? 'narrow' : 'normal', rest.sx)}
                    >
                      {content}
                    </TableCell>
                  )
                })}
              </TableRow>
            )
          })}
          {pagination.persistedHeight && emptyRows > 0 && (
            <TableRow style={{ height: 24 * emptyRows }}>
              <TableCell colSpan={6} />
            </TableRow>
          )}
        </TableBody>
        {pagination && (
          <TableFooter>
            <TableRow>
              <TablePagination
                colSpan={100}
                count={rows.length}
                rowsPerPageOptions={pagination.rowsPerPage}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </TableRow>
          </TableFooter>
        )}
      </MUITable>
    </ContainerComponent>
  )
}

const CellShape = PropTypes.shape({
  content: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  narrow: PropTypes.bool,
  onClick: PropTypes.func,
})

Table.propTypes = {
  border: PropTypes.bool,
  container: PropTypes.object,
  headers: PropTypes.arrayOf(CellShape),
  hover: PropTypes.bool,
  index: PropTypes.bool,
  pagination: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.shape({
      rowsPerPage: PropTypes.arrayOf(
        PropTypes.oneOfType([
          PropTypes.number,
          PropTypes.shape({
            label: PropTypes.string.isRequired,
            value: PropTypes.number.isRequired,
          }),
        ])
      ),
    }),
  ]),
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      cells: PropTypes.arrayOf(CellShape).isRequired,
      index: PropTypes.number,
    })
  ),
  selectable: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.shape({ multiselect: PropTypes.bool.isRequired }),
  ]),
  tableId: PropTypes.string,
  onSelect: PropTypes.func,
}

export default Table
